import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Send, Loader2, User, Scroll, Mic, MicOff, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';

const API = `${window.location.origin}/api`;

// Common spelling corrections dictionary
const spellingFixes = {
  'teh': 'the', 'thier': 'their', 'recieve': 'receive', 'beleive': 'believe',
  'occured': 'occurred', 'untill': 'until', 'seperate': 'separate',
  'definately': 'definitely', 'goverment': 'government', 'arguement': 'argument',
  'occassion': 'occasion', 'neccessary': 'necessary', 'accomodate': 'accommodate',
  'acheive': 'achieve', 'accross': 'across', 'agressive': 'aggressive',
  'apparantly': 'apparently', 'basicly': 'basically', 'becuase': 'because',
  'begining': 'beginning', 'calender': 'calendar', 'catagory': 'category',
  'commited': 'committed', 'comparision': 'comparison', 'concious': 'conscious',
  'copywrite': 'copyright', 'critisism': 'criticism', 'curiousity': 'curiosity',
  'deceive': 'deceive', 'desparate': 'desperate', 'diffrent': 'different',
  'dissapear': 'disappear', 'dissapoint': 'disappoint', 'embarass': 'embarrass',
  'enviroment': 'environment', 'exagerate': 'exaggerate', 'excercise': 'exercise',
  'existance': 'existence', 'experiance': 'experience', 'facinating': 'fascinating',
  'finaly': 'finally', 'foriegn': 'foreign', 'fourty': 'forty', 'freind': 'friend',
  'gaurd': 'guard', 'happend': 'happened', 'harrass': 'harass', 'heighth': 'height',
  'heros': 'heroes', 'humourous': 'humorous', 'hygeine': 'hygiene', 'ignorence': 'ignorance',
  'imaginery': 'imaginary', 'immediatly': 'immediately', 'independant': 'independent',
  'innoculate': 'inoculate', 'intellegent': 'intelligent', 'intresting': 'interesting',
  'irresistable': 'irresistible', 'knowlege': 'knowledge', 'libary': 'library',
  'liscense': 'license', 'manuever': 'maneuver', 'medeval': 'medieval',
  'millenia': 'millennia', 'minature': 'miniature', 'mischevous': 'mischievous',
  'misspell': 'misspell', 'momento': 'memento', 'naturaly': 'naturally',
  'noticable': 'noticeable', 'occurence': 'occurrence', 'offical': 'official',
  'oportunity': 'opportunity', 'optimisim': 'optimism', 'orignal': 'original',
  'outragous': 'outrageous', 'parliment': 'parliament', 'particualr': 'particular',
  'passtime': 'pastime', 'perseverence': 'perseverance', 'persue': 'pursue',
  'posession': 'possession', 'potatos': 'potatoes', 'preceed': 'precede',
  'prefered': 'preferred', 'privelege': 'privilege', 'probaly': 'probably',
  'proffessor': 'professor', 'prominant': 'prominent', 'pronounciation': 'pronunciation',
  'publically': 'publicly', 'realy': 'really', 'reccomend': 'recommend',
  'refered': 'referred', 'relevent': 'relevant', 'religous': 'religious',
  'remeber': 'remember', 'repetative': 'repetitive', 'resistence': 'resistance',
  'restaraunt': 'restaurant', 'rythm': 'rhythm', 'sacrilegious': 'sacrilegious',
  'shedule': 'schedule', 'sieze': 'seize', 'similer': 'similar', 'sincerly': 'sincerely',
  'speach': 'speech', 'succesful': 'successful', 'supercede': 'supersede',
  'suprise': 'surprise', 'temperture': 'temperature', 'tendancy': 'tendency',
  'therefor': 'therefore', 'tomatos': 'tomatoes', 'tommorow': 'tomorrow',
  'tounge': 'tongue', 'truely': 'truly', 'tyrany': 'tyranny', 'underate': 'underrate',
  'unfortunatly': 'unfortunately', 'unnecesary': 'unnecessary', 'usualy': 'usually',
  'vaccuum': 'vacuum', 'vegatable': 'vegetable', 'vehical': 'vehicle',
  'visious': 'vicious', 'wether': 'whether', 'wierd': 'weird', 'writting': 'writing',
  'dont': "don't", 'cant': "can't", 'wont': "won't", 'didnt': "didn't",
  'doesnt': "doesn't", 'isnt': "isn't", 'wasnt': "wasn't", 'werent': "weren't",
  'havent': "haven't", 'hasnt': "hasn't", 'hadnt': "hadn't", 'wouldnt': "wouldn't",
  'couldnt': "couldn't", 'shouldnt': "shouldn't", 'im': "I'm", 'ive': "I've",
  'youre': "you're", 'youve': "you've", 'theyre': "they're", 'theyve': "they've",
  'weve': "we've", 'id': "I'd", 'youd': "you'd", 'hed': "he'd", 'shed': "she'd",
  'wed': "we'd", 'theyd': "they'd", 'ill': "I'll", 'youll': "you'll",
  'itll': "it'll", 'theyll': "they'll", 'lets': "let's",
  'heres': "here's", 'theres': "there's", 'wheres': "where's", 'whats': "what's",
  'whos': "who's", 'hows': "how's", 'thats': "that's"
};

// Real-time auto-correct as user types
const autoCorrectWhileTyping = (text) => {
  if (!text) return text;
  
  let corrected = text;
  
  // Capitalize first letter
  if (corrected.length > 0) {
    corrected = corrected.charAt(0).toUpperCase() + corrected.slice(1);
  }
  
  // Capitalize 'i' when standalone (surrounded by spaces or at boundaries)
  corrected = corrected.replace(/(\s|^)i(\s|$)/g, '$1I$2');
  corrected = corrected.replace(/(\s|^)i([''])/g, '$1I$2');
  
  // Fix spelling for completed words (followed by space or punctuation)
  Object.entries(spellingFixes).forEach(([wrong, right]) => {
    const regex = new RegExp(`\\b${wrong}\\b(?=\\s|[.,!?;:]|$)`, 'gi');
    corrected = corrected.replace(regex, (match) => {
      if (match[0] === match[0].toUpperCase()) {
        return right.charAt(0).toUpperCase() + right.slice(1);
      }
      return right;
    });
  });
  
  return corrected;
};

// Final auto-correct when sending (adds punctuation)
const autoCorrectOnSend = (text) => {
  if (!text || !text.trim()) return text;
  
  let corrected = autoCorrectWhileTyping(text.trim());
  
  // Add period at end if no punctuation
  if (!/[.!?]$/.test(corrected)) {
    corrected += '.';
  }
  
  return corrected;
};

const DebateRoom = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const [debate, setDebate] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const textareaRef = useRef(null);
  const hasSentMessage = useRef(false);

  // Auto-focus textarea when AI finishes responding
  useEffect(() => {
    if (!loading && !initialLoading && hasSentMessage.current && textareaRef.current) {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.click();
        }
      }, 200);
    }
  }, [loading, initialLoading]);

  // Auto-resize textarea as user types
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 400) + 'px';
    }
  };

  useEffect(() => {
    loadDebate();
    initializeSpeechRecognition();
    
    // Add swipe gesture listener
    const handleTouchStart = (e) => {
      touchStartX.current = e.changedTouches[0].screenX;
    };
    
    const handleTouchEnd = (e) => {
      touchEndX.current = e.changedTouches[0].screenX;
      handleSwipe();
    };
    
    const handleSwipe = () => {
      // Swipe left (start > end) with at least 100px distance
      if (touchStartX.current - touchEndX.current > 100) {
        navigate('/debate');
      }
    };
    
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (synthRef.current) {
        synthRef.current.cancel();
      }
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [id, navigate]);

  useEffect(() => {
    scrollToBottom();
  }, [debate?.messages]);

  const initializeSpeechRecognition = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';
      
      recognition.onstart = () => {
        setIsListening(true);
      };
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInput(transcript);
        setIsListening(false);
      };
      
      recognition.onerror = (event) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Speech recognition error:', event.error);
        }
        setIsListening(false);
        if (event.error === 'no-speech') {
          toast.error('No speech detected. Please try again.');
        } else if (event.error === 'not-allowed') {
          toast.error('Microphone access denied. Please enable it in your browser settings.');
        } else {
          toast.error('Speech recognition error. Please try again.');
        }
      };
      
      recognition.onend = () => {
        setIsListening(false);
      };
      
      recognitionRef.current = recognition;
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('Speech recognition not supported');
      }
    }
  };

  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in your browser');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
      toast.info('Listening... Speak now');
    }
  };

  const speakText = (text) => {
    if (!voiceEnabled) {
      return;
    }
    
    // Cancel any ongoing speech
    synthRef.current.cancel();
    
    // Function to actually speak once voices are ready
    const speak = () => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95; // Slightly slower for clarity
      utterance.pitch = 1;
      utterance.volume = 1;
      
      // Try to use a natural-sounding voice
      const voices = synthRef.current.getVoices();
      
      const preferredVoice = voices.find(voice => 
        voice.name.includes('Natural') || 
        voice.name.includes('Premium') ||
        voice.name.includes('Google') ||
        voice.lang.startsWith('en')
      );
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      } else if (voices.length > 0) {
        utterance.voice = voices[0];
      }
      
      utterance.onerror = (e) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Speech error:', e);
        }
      };
      
      synthRef.current.speak(utterance);
    };
    
    // Ensure voices are loaded
    const voices = synthRef.current.getVoices();
    if (voices.length > 0) {
      speak();
    } else {
      // Wait for voices to load
      synthRef.current.addEventListener('voiceschanged', () => {
        speak();
      }, { once: true });
      
      // Fallback after 1 second
      setTimeout(() => {
        if (synthRef.current.getVoices().length > 0) {
          speak();
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.error('No voices available');
          }
          toast.error('Text-to-speech not available in your browser');
        }
      }, 1000);
    }
  };

  const toggleVoice = () => {
    const newState = !voiceEnabled;
    setVoiceEnabled(newState);
    
    if (!newState) {
      synthRef.current.cancel();
      toast.info('AI voice disabled');
    } else {
      // Test voice with a short message
      toast.success('AI voice enabled - I will speak my responses');
      
      // Load voices if needed
      const loadVoices = () => {
        synthRef.current.getVoices();
      };
      
      if (synthRef.current.getVoices().length === 0) {
        synthRef.current.addEventListener('voiceschanged', loadVoices, { once: true });
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Refocus on input after scroll
    setTimeout(() => {
      if (textareaRef.current && !loading) {
        textareaRef.current.focus();
      }
    }, 100);
  };

  const loadDebate = async () => {
    try {
      const response = await axios.get(`${API}/debate/${id}`, { withCredentials: true });
      setDebate(response.data);
    } catch (error) {
      toast.error('Failed to load debate');
      navigate('/debate');
    } finally {
      setInitialLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = autoCorrectOnSend(input.trim());
    setInput('');
    hasSentMessage.current = true; // Mark that user has sent a message
    
    // Reset textarea height after sending
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    // Optimistically add user message
    setDebate(prev => ({
      ...prev,
      messages: [...prev.messages, { role: 'user', content: userMessage }]
    }));
    
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/debate/message`,
        { debate_id: id, message: userMessage },
        { withCredentials: true }
      );

      // Add AI response
      const aiResponse = response.data.response;
      setDebate(prev => ({
        ...prev,
        messages: [...prev.messages, { role: 'assistant', content: aiResponse }]
      }));
      
      // Speak AI response if voice enabled
      speakText(aiResponse);
    } catch (error) {
      toast.error('Failed to send message');
      // Remove optimistic message on error
      setDebate(prev => ({
        ...prev,
        messages: prev.messages.slice(0, -1)
      }));
    } finally {
      setLoading(false);
      // Keep focus on input for continuous conversation flow
      // Use setTimeout to ensure React has re-rendered with loading=false
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 50);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg">
        <Loader2 className="w-12 h-12 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!debate) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg">
        <Card className="card-glow p-8 text-center">
          <h2 className="text-2xl font-bold text-amber-900 mb-4">Debate not found</h2>
          <Button onClick={() => navigate('/debate')} className="bg-amber-600 hover:bg-amber-700">
            Back to Debates
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg flex flex-col">
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-black/90 border-amber-900/50' : 'bg-white/80 border-amber-200'} backdrop-blur-sm border-b shadow-sm`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/debate')}
              className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
              data-testid="back-to-debates-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <Scroll className={`w-5 h-5 ${isDarkMode ? 'text-amber-500' : 'text-amber-700'}`} />
                <h1 className={`text-lg font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Debate Session</h1>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{debate.topic}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Debate Messages */}
      <div className="flex-1 container mx-auto px-4 py-6 pb-16 overflow-hidden flex flex-col">
        
        <Card className="card-glow flex-1 border-amber-200 shadow-lg overflow-hidden flex flex-col" data-testid="debate-container">
          <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-hide">
            {/* Position Selection Prompt */}
            {!debate.user_position && debate.messages.length === 0 && (
              <div className="flex justify-center items-center h-full">
                <Card className="card-glow max-w-lg p-8 border-amber-300 shadow-xl bg-gradient-to-br from-amber-50 to-orange-50">
                  <h3 className="text-2xl font-bold text-amber-900 mb-4 text-center">Choose Your Position</h3>
                  <p className="text-amber-800 mb-6 text-center">Which side will you argue in this debate?</p>
                  <p className="text-sm text-amber-700 mb-6 text-center italic font-semibold">"{debate.topic}"</p>
                  <div className="space-y-3">
                    <Button
                      onClick={() => setUserPosition('for')}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
                      data-testid="select-position-for"
                    >
                      <div className="text-left">
                        <p className="font-bold">For / In Favor</p>
                        <p className="text-sm opacity-90">Argue in support of the proposition</p>
                      </div>
                    </Button>
                    <Button
                      onClick={() => setUserPosition('against')}
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg"
                      data-testid="select-position-against"
                    >
                      <div className="text-left">
                        <p className="font-bold">Against / Opposed</p>
                        <p className="text-sm opacity-90">Argue against the proposition</p>
                      </div>
                    </Button>
                  </div>
                </Card>
              </div>
            )}
            
            {debate.messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`debate-message-${index}`}
              >
                <div
                  className={`flex items-start space-x-3 max-w-full sm:max-w-[80%] min-w-0 ${
                    message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}
                >
                  {message.role === 'user' ? (
                    user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt="You" 
                        className="flex-shrink-0 w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-amber-600">
                        <User className="w-5 h-5 text-white" />
                      </div>
                    )
                  ) : (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-purple-600">
                      <Scroll className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-3 min-w-0 overflow-hidden ${
                      message.role === 'user'
                        ? 'bg-amber-600 text-white'
                        : 'bg-white border-2 border-purple-300 text-purple-900'
                    }`}
                  >
                    <p className={`text-sm whitespace-pre-wrap leading-relaxed break-words ${
                      message.role !== 'user' ? 'font-medium' : ''
                    }`}>{message.content}</p>
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="debate-loading">
                <div className="flex items-start space-x-3 max-w-full sm:max-w-[80%]">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center">
                    <Scroll className="w-5 h-5 text-white" />
                  </div>
                  <div className="rounded-2xl px-4 py-3 bg-white border-2 border-purple-300">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className={`border-t p-4 backdrop-blur-sm ${isDarkMode ? 'border-amber-700 bg-black' : 'border-amber-200 bg-white/50'}`}>
            <form onSubmit={sendMessage} className="flex space-x-2">
              <Button
                type="button"
                onClick={toggleListening}
                className={`${isListening ? 'bg-red-500 hover:bg-red-400 animate-pulse' : isDarkMode ? '!bg-amber-500 hover:!bg-amber-400 !text-amber-950' : 'bg-gray-600 hover:bg-gray-700'}`}
                disabled={loading}
                data-testid="mic-btn"
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </Button>
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => {
                  const textarea = e.target;
                  const cursorPos = textarea.selectionStart;
                  const originalValue = e.target.value;
                  const corrected = autoCorrectWhileTyping(originalValue);
                  const diff = corrected.length - originalValue.length;
                  
                  setInput(corrected);
                  adjustTextareaHeight();
                  
                  // Restore cursor position after React re-render
                  requestAnimationFrame(() => {
                    if (textareaRef.current) {
                      const newPos = cursorPos + diff;
                      textareaRef.current.setSelectionRange(newPos, newPos);
                    }
                  });
                }}
                placeholder={isListening ? "Listening..." : "Present your argument or click mic to speak..."}
                className="flex-1 border-amber-200 focus:border-amber-400 resize-none min-h-[60px] overflow-y-auto"
                style={{ maxHeight: '400px' }}
                disabled={loading || isListening}
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-form-type="other"
                data-lpignore="true"
                data-testid="debate-input"
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (input.trim() && !loading) {
                      sendMessage(e);
                    }
                  }
                }}
              />
              <Button
                type="button"
                onClick={toggleVoice}
                className={`${voiceEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 hover:bg-gray-500'}`}
                data-testid="toggle-voice-btn"
                title={voiceEnabled ? 'AI Voice On' : 'AI Voice Off'}
              >
                {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </Button>
              <Button
                type="submit"
                disabled={!input.trim() || loading}
                className={`${isDarkMode ? '!bg-amber-500 hover:!bg-amber-400 !text-amber-950 !opacity-100' : 'bg-amber-600 hover:bg-amber-700'}`}
                data-testid="debate-send-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </form>
            {isListening && (
              <p className="text-sm text-amber-600 mt-2 italic">🎤 Listening... Speak clearly</p>
            )}
          </div>

          {/* Exit Button at Bottom */}
          <div className={`border-t p-4 ${isDarkMode ? 'border-amber-700 bg-black' : 'border-amber-200 bg-amber-50/50'}`}>
            <Button
              onClick={() => navigate('/debate')}
              variant="outline"
              className={`w-full ${isDarkMode ? 'border-amber-400 text-amber-400 hover:bg-amber-900/30' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}
              data-testid="exit-debate-bottom-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Exit Debate Room
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DebateRoom;